// Angular imports
import { OnInit, Output, Input, EventEmitter, HostBinding, Directive, ElementRef } from '@angular/core';
import { PercentPipe } from '@angular/common';

// Import column and selection types
import { Column } from '../../types';
import { Rect } from '../types';
import { CalculationSelection } from 'src/app/state-management/calculation-state';

@Directive()
export abstract class CellComponent<T> implements OnInit {
    // The value of this cell
    @Input() public value?: T;

    // Whether this cell is readonly or not
    @Input() public readonly: boolean = false;

    // An optional default value for the cell
    @Input() public default?: T;

    // The row this cell is in
    @Input() public rowIndex!: number;

    // The current selection
    @Input() public selection!: CalculationSelection;

    // Whether or not to select the cell's content on focus
    @Input() public selectOnFocus: boolean = true;

    // Event handlers for common events
    @Output() public onFocus = new EventEmitter<Event>();
    @Output() public onBlur = new EventEmitter<Event>();
    @Output() public valueChange = new EventEmitter<T>();
    @Output() public selectionChange = new EventEmitter<CalculationSelection>();

    // Event handlers for moving selection
    @Output() public selectAbove = new EventEmitter<boolean>();
    @Output() public selectBelow = new EventEmitter<boolean>();
    @Output() public selectLeft = new EventEmitter<boolean>();
    @Output() public selectRight = new EventEmitter<boolean>();

    // Host binding for setting width
    @HostBinding('style.width') public _width!: string | number | null;

    // Protected properites
    protected hasShallowFocus: boolean | null = null;

    // Internal properties
    private _column!: Column;

    constructor(private hostElementRef: ElementRef, private percentPipe: PercentPipe) {}

    /* --- Getters and setters */

    @Input() public set column(value: Column) {
        // Set column
        this._column = value;

        // Update with
        this._width = this.percentPipe.transform(this._column?.width ?? 0.10, '1.0-6');
    }

    public get column(): Column {
        return this._column;
    }

    protected get isSelected(): boolean {
        return !!this.selection && this.selection.current.row === this.rowIndex && this.selection.current.column === this.column.index;
    }

    /* --- Functions --- */

    public ngOnInit(): void {
        // Set cell to readonly if the column is not focusable
        this.readonly = this.readonly || !this.column?.focusable;

        // Assign width
        this._width = this.percentPipe.transform(this._column?.width ?? 0.10, '1.0-6');
    }

    public getRelativeBoundingRect(): Rect {
        // Get client bounding rect for size
        const clientBoundingRect = this.hostElementRef.nativeElement.getBoundingClientRect() as DOMRect;

        // Return rect
        return {
            top: this.hostElementRef.nativeElement.offsetTop,
            left: this.hostElementRef.nativeElement.offsetLeft,
            width: clientBoundingRect.width,
            height: clientBoundingRect.height
        }
    }

    /**
     * Place the cursor on this cell's content
     * @param shallow Whether to use shallow focus or not
     * @returns void
     */
    public abstract focus(shallow?: boolean): void;

    /**
     * Remove focus from this cell's content. This does not remove the selection
     * @returns void
     */
    public abstract blur(): void;

    /**
     * Select the cell.
     * @returns void
     */
    public abstract select(): void;
    public abstract select(setOrigin?: boolean): void;

    /**
     * Select all of the cell's content.
     * @returns void
     */
    public abstract selectContent(): void;
}